import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';

const API = process.env.REACT_APP_BACKEND_URL;
const apiBase = `${API}/api`;

const DIMENSIONS = [
  { v: 'all', l: 'Alle' },
  { v: 'municipal', l: 'Gemeenten' },
  { v: 'camping', l: 'Campings' },
  { v: 'hotel', l: 'Hotels' },
  { v: 'spa', l: 'Spa / Wellness' },
  { v: 'school', l: 'Schoolzwembaden' },
  { v: 'fitness', l: 'Fitness / Sport' },
  { v: 'private', l: 'Privé' },
  { v: 'commercial', l: 'Commercieel' },
];

/**
 * Social Proof Settings — beheer manual social-proof regels (worden door de
 * Sequence Generator letterlijk gebruikt in stap 4) + toont auto-derived lines
 * uit echte data (orders + positieve replies).
 */
export default function SocialProofSettings() {
  const [manual, setManual] = useState([]);
  const [computed, setComputed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [dimension, setDimension] = useState('all');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${apiBase}/profile/social-proof`, { credentials: 'include' });
      const d = await r.json();
      if (r.ok) {
        setManual(d.manual || []);
        setComputed(d.computed || null);
      }
    } catch { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const addLine = async () => {
    const t = (text || '').trim();
    if (t.length < 5) { toast.error('Tekst is te kort (min 5 tekens)'); return; }
    if (t.length > 280) { toast.error('Tekst is te lang (max 280 tekens)'); return; }
    setSaving(true);
    try {
      const r = await fetch(`${apiBase}/profile/social-proof`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: t, dimension }),
      });
      const d = await r.json();
      if (!r.ok || !d.ok) throw new Error(d.detail || d.error || 'mislukt');
      toast.success('Toegevoegd');
      setText('');
      load();
    } catch (e) { toast.error(e.message || 'Toevoegen mislukt'); }
    setSaving(false);
  };

  const removeLine = async (id) => {
    try {
      const r = await fetch(`${apiBase}/profile/social-proof/${id}`, {
        method: 'DELETE', credentials: 'include',
      });
      const d = await r.json();
      if (d.ok) { toast.success('Verwijderd'); load(); }
    } catch (e) { toast.error(e.message); }
  };

  if (loading) {
    return <div className="border border-zinc-200 rounded-md p-4 text-sm text-zinc-500">Social proof laden...</div>;
  }

  return (
    <div className="border border-zinc-200 rounded-md bg-white p-5 mb-4" data-testid="social-proof-settings">
      <div className="mb-4">
        <h3 className="font-heading text-base font-semibold flex items-center gap-2">
          🏆 Social Proof Library
        </h3>
        <p className="text-xs text-zinc-500 mt-1">
          De Sequence Generator gebruikt deze regels letterlijk in stap 4 (Social Proof).
          Voeg concrete, verifieerbare claims toe — bv. <em>"12 gemeentelijke zwembaden gebruiken PoolFIX sinds 2024"</em>.
          Geen verzonnen cijfers!
        </p>
      </div>

      {/* Manual entries */}
      <div className="mb-4">
        <label className="text-[11px] font-semibold uppercase tracking-wide text-zinc-600 block mb-1.5">
          Handmatige regels ({manual.length})
        </label>
        {manual.length === 0 ? (
          <p className="text-[11px] text-zinc-400 italic mb-2">Nog geen regels — voeg minstens 1 toe voor maximale conversie</p>
        ) : (
          <div className="space-y-1.5 mb-2">
            {manual.map((m) => (
              <div key={m.id} className="flex items-start gap-2 px-3 py-2 bg-emerald-50/60 border border-emerald-200 rounded-sm"
                   data-testid={`proof-row-${m.id}`}>
                <span className="text-[10px] font-bold uppercase tracking-wide text-emerald-700 px-1.5 py-0.5 bg-white rounded">
                  {DIMENSIONS.find((x) => x.v === m.dimension)?.l || m.dimension || 'Alle'}
                </span>
                <span className="flex-1 text-[12px] text-zinc-800">{m.text}</span>
                <button onClick={() => removeLine(m.id)} className="text-zinc-400 hover:text-rose-600 text-base leading-none"
                        aria-label="Verwijder" data-testid={`proof-remove-${m.id}`}>×</button>
              </div>
            ))}
          </div>
        )}
        <div className="flex flex-col md:flex-row gap-2">
          <select value={dimension} onChange={(e) => setDimension(e.target.value)}
                  className="px-2 py-1 text-sm border border-zinc-300 rounded-sm bg-white"
                  data-testid="proof-dimension-select">
            {DIMENSIONS.map((d) => <option key={d.v} value={d.v}>{d.l}</option>)}
          </select>
          <Input
            type="text"
            placeholder="bv. 12 gemeentelijke zwembaden gebruiken PoolFIX al sinds 2024"
            value={text} onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addLine(); } }}
            className="text-sm flex-1" maxLength={280}
            data-testid="proof-text-input"
          />
          <Button onClick={addLine} size="sm" disabled={saving || !text.trim()}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="proof-add-btn">
            Toevoegen
          </Button>
        </div>
        <p className="text-[10px] text-zinc-400 mt-1">{text.length}/280 tekens</p>
      </div>

      {/* Computed lines */}
      {computed && (computed.lines || []).length > 0 && (
        <div className="border-t border-zinc-100 pt-3">
          <label className="text-[11px] font-semibold uppercase tracking-wide text-zinc-600 block mb-1.5">
            📊 Auto-afgeleid uit jouw data
          </label>
          <p className="text-[11px] text-zinc-500 italic mb-1.5">
            Deze regels worden automatisch berekend uit echte orders + positief geclassificeerde replies.
            Ze worden samen met de manual lijst gebruikt door de AI.
          </p>
          <div className="space-y-1">
            {computed.lines.filter((l) => l.source === 'auto').map((l, i) => (
              <div key={i} className="flex items-center gap-2 px-3 py-1.5 bg-sky-50 border border-sky-200 rounded-sm">
                <span className="text-[10px] font-bold uppercase text-sky-700 px-1.5 py-0.5 bg-white rounded">
                  AUTO · {DIMENSIONS.find((x) => x.v === l.dimension)?.l || l.dimension || 'Alle'}
                </span>
                <span className="text-[12px] text-zinc-800 flex-1">{l.text}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
